import type { Rating } from "../types/rating";
import { isUnknownArray, isTimestamp } from "../types/data";
import {
  CHALLENGE_CONTINENT_GROUP_KEYS,
  countryBelongsToGroup,
  getCountryGroupCodes
} from "./countryGroups";
import { getCountryCode } from "./countryNames";
import {
  deduplicateRatingsByMediaId,
  getMediaType as getProgressMediaType,
  getRuntimeMinutes,
  getWatchedRuntimeMinutes
} from "./gameProgressEligibility";
import { getLanguageCode } from "./languageNames";
import {
  getPersonIdentity,
  getRatingActors,
  getRatingComposers,
  getRatingDirectors
} from "./ratingPeople";

const normalizeText = (value: unknown) => String(value || "").toLowerCase().replace(/ё/g, "е").trim();
const asArray = <T>(value: T | T[] | null | undefined): T[] => isUnknownArray(value) ? value : value ? [value] : [];

const getGenres = (rating: Rating) => asArray(rating?.genres || rating?.filmGenres)
  .map((item) => normalizeText(typeof item === "string" ? item : item?.name)).filter(Boolean);
const getCountries = (rating: Rating) => [
  ...asArray(rating?.originCountry),
  ...asArray(rating?.productionCountry),
  ...asArray(rating?.productionCountries)
].map(getCountryCode).filter(Boolean);
const getDirectors = (rating: Rating) =>
  getRatingDirectors(rating).map(getPersonIdentity).filter(Boolean);
const getActors = (rating: Rating) =>
  getRatingActors(rating).map(getPersonIdentity).filter(Boolean);
const getComposers = (rating: Rating) =>
  getRatingComposers(rating).map(getPersonIdentity).filter(Boolean);
const getYear = (rating: Rating) => {
  const raw = rating?.releaseYear || rating?.releaseDate || rating?.filmReleaseDate || rating?.first_air_date;
  const year = Number(String(raw || "").slice(0, 4));
  return Number.isFinite(year) ? year : null;
};
const getOriginalLanguages = (rating: Rating) => {
  const directLanguage = [
    rating?.original_language,
    rating?.originalLanguage,
    rating?.language
  ].map(getLanguageCode).find(Boolean);
  if (directLanguage) return [directLanguage];

  const spokenLanguage = asArray(rating?.spoken_languages)
    .map(getLanguageCode)
    .find(Boolean);
  return spokenLanguage ? [spokenLanguage] : [];
};
const getRatingDay = (rating: Rating) => {
  const value = rating?.firstTrackedAt || rating?.firstRatedAt || rating?.ratedAt;
  const date = isTimestamp(value) ? value.toDate() : value instanceof Date ? new Date(value) :
    value && (typeof value === "string" || typeof value === "number") ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toISOString().slice(0, 10) : null;
};
const count = (predicate: (rating: Rating) => boolean = () => true) => (ratings: Rating[]) => ratings.filter(predicate).length;

// Assign rating titles to requirement slots through a small flow graph. This
// is deliberately stricter than summing independent Set sizes: every title has
// capacity 1, so a US/Japan co-production can advance one regional requirement
// in a challenge, never two. Feature nodes also have capacity 1, preventing two
// different ratings for the same country/genre/year from filling duplicate
// "unique" slots.
type ChallengeFeature = string | number | null | undefined;
type FeatureExtractor = (rating: Rating) => ChallengeFeature[];
interface ChallengeRequirement {
  id?: string;
  dimension?: string;
  limit: number;
  unique?: boolean;
  extractor?: FeatureExtractor;
}
interface FlowEdge { to: number; reverse: number; capacity: number; }

const getRequirementProgress = (ratings: Rating[], requirements: ChallengeRequirement[]) => {
  const source = 0;
  const sink = 1;
  const graph: FlowEdge[][] = [[], []];
  const nodeByKey = new Map<string, number>();
  const featureCategoryEdges = new Set<string>();

  const addNode = (key: string) => {
    const existingNode = nodeByKey.get(key);
    if (existingNode !== undefined) return existingNode;
    const node = graph.length;
    graph.push([]);
    nodeByKey.set(key, node);
    return node;
  };
  const addEdge = (from: number, to: number, capacity: number) => {
    const forward = { to, reverse: graph[to].length, capacity };
    const backward = { to: from, reverse: graph[from].length, capacity: 0 };
    graph[from].push(forward);
    graph[to].push(backward);
  };

  const normalizedRequirements = asArray(requirements)
    .map((requirement, index) => ({
      ...requirement,
      id: String(requirement?.id || `requirement-${index}`),
      limit: Math.max(0, Number(requirement?.limit) || 0)
    }))
    .filter((requirement) => requirement.limit > 0);

  normalizedRequirements.forEach((requirement) => {
    const categoryNode = addNode(`category:${requirement.id}`);
    addEdge(categoryNode, sink, requirement.limit);
  });

  asArray(ratings).forEach((rating: Rating, ratingIndex) => {
    const filmNode = addNode(`film:${ratingIndex}`);
    addEdge(source, filmNode, 1);

    normalizedRequirements.forEach((requirement) => {
      const values = [...new Set(
        asArray(requirement.extractor?.(rating)).filter((value) =>
          value !== null && value !== undefined && String(value) !== ""
        ).map(String)
      )];
      if (!values.length) return;

      const categoryNode = addNode(`category:${requirement.id}`);
      if (requirement.unique === false) {
        addEdge(filmNode, categoryNode, 1);
        return;
      }

      values.forEach((value) => {
        const featureKey = `${requirement.dimension || requirement.id}:${value}`;
        const featureIn = addNode(`feature-in:${featureKey}`);
        const featureOut = addNode(`feature-out:${featureKey}`);
        if (!graph[featureIn].some((edge) => edge.to === featureOut)) {
          addEdge(featureIn, featureOut, 1);
        }
        addEdge(filmNode, featureIn, 1);
        const categoryEdgeKey = `${featureKey}:${requirement.id}`;
        if (!featureCategoryEdges.has(categoryEdgeKey)) {
          featureCategoryEdges.add(categoryEdgeKey);
          addEdge(featureOut, categoryNode, 1);
        }
      });
    });
  });

  let flow = 0;
  while (true) {
    const levels: number[] = new Array<number>(graph.length).fill(-1);
    levels[source] = 0;
    const queue = [source];
    for (let index = 0; index < queue.length; index += 1) {
      const node = queue[index];
      graph[node].forEach((edge) => {
        if (edge.capacity > 0 && levels[edge.to] < 0) {
          levels[edge.to] = levels[node] + 1;
          queue.push(edge.to);
        }
      });
    }
    if (levels[sink] < 0) break;

    const positions: number[] = new Array<number>(graph.length).fill(0);
    const send = (node: number, available: number): number => {
      if (node === sink) return available;
      for (; positions[node] < graph[node].length; positions[node] += 1) {
        const edge = graph[node][positions[node]];
        if (edge.capacity <= 0 || levels[edge.to] !== levels[node] + 1) continue;
        const pushed = send(edge.to, Math.min(available, edge.capacity));
        if (!pushed) continue;
        edge.capacity -= pushed;
        graph[edge.to][edge.reverse].capacity += pushed;
        return pushed;
      }
      return 0;
    };

    while (true) {
      const pushed = send(source, Number.MAX_SAFE_INTEGER);
      if (!pushed) break;
      flow += pushed;
    }
  }

  return flow;
};

const unique = (extractor: FeatureExtractor, dimension = "value") => (ratings: Rating[]) =>
  getRequirementProgress(ratings, [{
    id: `unique-${dimension}`,
    dimension,
    limit: ratings.length,
    extractor
  }]);
const maxFrequency = (extractor: FeatureExtractor) => (ratings: Rating[]) => {
  const frequencies = new Map<ChallengeFeature, number>();
  ratings.forEach((rating: Rating) => new Set(extractor(rating)).forEach((value) => {
    frequencies.set(value, (frequencies.get(value) || 0) + 1);
  }));
  return Math.max(0, ...frequencies.values());
};
const genreCount = (...names: string[]) => {
  const wanted = names.map(normalizeText);
  return count((rating: Rating) => getGenres(rating).some((item) => wanted.includes(item)));
};
const genreSet = (...groups: (string | string[])[]) => (ratings: Rating[]) => getRequirementProgress(
  ratings,
  groups.map((group, index) => {
    const wanted = asArray(group).map(normalizeText);
    return {
      id: `genre-group-${index}`,
      limit: 1,
      unique: false,
      extractor: (rating: Rating) => getGenres(rating).some((genre) => wanted.includes(genre))
        ? [index]
        : []
    };
  })
);

const countryMatchesAlias = (country: string, alias: string) =>
  alias.length <= 3 ? country === alias : country.includes(alias);
const countryKey = (country: string) => getCountryCode(country);
const US_ALIASES = ["us", "usa", "сша", "united states", "соединенные штаты"];
const uniqueCountriesIn = (groupKey: string) => unique((rating: Rating) =>
  getCountries(rating)
    .filter((country) => countryBelongsToGroup(country, groupKey))
    .map(countryKey)
, "country");
const nonUsFilms = count((rating: Rating) => !getCountries(rating).some((item) =>
  getCountryCode(item) === "US" || US_ALIASES.some((alias) => countryMatchesAlias(item, alias))
));
const groupFilmCount = (groupKey: string) => count((rating: Rating) =>
  getCountries(rating).some((country) => countryBelongsToGroup(country, groupKey))
);
const countryFilmCount = (...aliases: string[]) => {
  const wanted = aliases.map(normalizeText);
  return count((rating: Rating) => getCountries(rating).some((country) =>
    wanted.some((alias) =>
      getCountryCode(country) === String(alias).toUpperCase() ||
      countryMatchesAlias(country, alias)
    )
  ));
};
const yearRangeCount = (from: number, to: number) => count((rating: Rating) => {
  const year = getYear(rating);
  return Number(year) >= Number(from) && Number(year) <= Number(to);
});
const decadeFilmCount = (decade: number) => count((rating: Rating) => {
  const year = getYear(rating);
  return year ? Math.floor(year / 10) * 10 === Number(decade) : false;
});
const mediaCount = (mediaType: string) => (ratings: Rating[]) =>
  ratings.filter((rating: Rating) => getProgressMediaType(rating) === mediaType).length;
const runtimeCount = (minMinutes: number) => count((rating: Rating) => Number(getRuntimeMinutes(rating)) >= Number(minMinutes));
const totalRuntimeHours = (ratings: Rating[]) =>
  Math.floor(ratings.reduce(
    (sum, rating: Rating) => sum + Math.min(60, getWatchedRuntimeMinutes(rating)),
    0
  ) / 60);
const getProgressRating = (rating: Rating) =>
  rating?.progressUserRate ?? rating?.userRate;
const highRatingCount = (minRating: number) =>
  count((rating: Rating) => Number(getProgressRating(rating)) >= Number(minRating));
const exactRatingCount = (ratingValue: number) =>
  count((rating: Rating) => Number(getProgressRating(rating)) === Number(ratingValue));
const uniqueDecades = unique((rating: Rating) => { const year = getYear(rating); return year ? [Math.floor(year / 10) * 10] : []; }, "decade");
const uniqueYears = unique((rating: Rating) => { const year = getYear(rating); return year ? [year] : []; }, "year");
const uniqueGenres = unique(getGenres, "genre");
const uniqueCountries = unique((rating: Rating) => getCountries(rating).map(countryKey), "country");
const uniqueLanguages = unique(getOriginalLanguages, "language");
const sameActor = maxFrequency(getActors);
const sameDirector = maxFrequency(getDirectors);
const sameComposer = maxFrequency(getComposers);

const uniqueRequirement = (id: string, dimension: string, limit: number, extractor: FeatureExtractor): ChallengeRequirement => ({
  id,
  dimension,
  limit,
  extractor
});
const genericRequirement = (id: string, limit: number, predicate: (rating: Rating) => boolean = () => true) => ({
  id,
  limit,
  unique: false,
  extractor: (rating: Rating) => predicate(rating) ? [id] : []
});
const countryGroupRequirement = (id: string, groupKey: string, limit: number) =>
  uniqueRequirement(id, "country", limit, (rating: Rating) =>
    getCountries(rating).filter((country) => countryBelongsToGroup(country, groupKey))
  );

const mixedUniverse = (ratings: Rating[]) => getRequirementProgress(ratings, [
  uniqueRequirement("countries", "country", 20, getCountries),
  uniqueRequirement("genres", "genre", 20, getGenres),
  uniqueRequirement("decades", "decade", 10, (rating: Rating) => {
    const year = getYear(rating);
    return year ? [Math.floor(year / 10) * 10] : [];
  }),
  uniqueRequirement("directors", "director", 20, getDirectors)
]);
const curatorPath = (ratings: Rating[]) => getRequirementProgress(ratings, [
  genericRequirement("high-ratings", 10, (rating: Rating) => Number(getProgressRating(rating)) >= 8),
  uniqueRequirement("genres", "genre", 10, getGenres),
  uniqueRequirement("countries", "country", 10, getCountries)
]);
const worldSampler = (ratings: Rating[]) => getRequirementProgress(ratings, [
  countryGroupRequirement("europe", "europe", 5),
  countryGroupRequirement("asia", "asia", 5),
  countryGroupRequirement("north-america", "northAmerica", 3),
  countryGroupRequirement("south-america", "southAmerica", 3)
]);
const grandWorldSampler = (ratings: Rating[]) => getRequirementProgress(ratings, [
  countryGroupRequirement("europe", "europe", 10),
  countryGroupRequirement("asia", "asia", 10),
  countryGroupRequirement("north-america", "northAmerica", 5),
  countryGroupRequirement("south-america", "southAmerica", 5),
  countryGroupRequirement("africa", "africa", 5)
]);
const livingEncyclopedia = (ratings: Rating[]) => getRequirementProgress(ratings, [
  genericRequirement("movies", 100),
  uniqueRequirement("countries", "country", 50, getCountries),
  uniqueRequirement("genres", "genre", 25, getGenres),
  uniqueRequirement("years", "year", 25, (rating: Rating) => {
    const year = getYear(rating);
    return year ? [year] : [];
  })
]);
const closedContinent = (ratings: Rating[]) => {
  const completed = CHALLENGE_CONTINENT_GROUP_KEYS.some((groupKey) =>
    getRequirementProgress(ratings, [countryGroupRequirement(
      `continent-${groupKey}`,
      groupKey,
      getCountryGroupCodes(groupKey).length
    )]) === getCountryGroupCodes(groupKey).length
  );
  return completed ? 1 : 0;
};

export const CHALLENGE_DIFFICULTIES = {
  easy: "Лёгкое",
  medium: "Нормальное",
  hard: "Сложное",
  extreme: "Очень сложное",
  impossible: "Невозможное"
};

export const CHALLENGE_TYPES = {
  activity: "Активность",
  countries: "Страны",
  genres: "Жанры",
  decades: "Десятилетия",
  actors: "Актёры",
  directors: "Режиссёры",
  composers: "Композиторы",
  years: "Годы",
  runtime: "Хронометраж",
  media: "Формат",
  ratings: "Оценки",
  languages: "Языки",
  mixed: "Смешанные"
};

export const CHALLENGE_EXPERIENCE_BY_DIFFICULTY = {
  easy: 10,
  medium: 40,
  hard: 100,
  extreme: 250,
  impossible: 500
};

export const getChallengeExperience = (difficulty: string | number) =>
  CHALLENGE_EXPERIENCE_BY_DIFFICULTY[difficulty as keyof typeof CHALLENGE_EXPERIENCE_BY_DIFFICULTY] || CHALLENGE_EXPERIENCE_BY_DIFFICULTY.easy;

const CHALLENGE_SCORING_RULES = {
  activity: "Каждый новый оценённый фильм или сериал даёт одно деление.",
  activityDays: "Засчитывается не больше одного фильма за календарный день; для каждого деления нужен новый день.",
  countries: "Каждый отдельный фильм из подходящей страны даёт одно деление.",
  uniqueCountries: "Для уникальных стран каждое деление требует отдельного фильма и отдельной подходящей страны.",
  genres: "Каждый отдельный фильм подходящего жанра даёт одно деление.",
  uniqueGenres: "Для разных жанров каждый фильм закрепляется только за одним жанром этого испытания.",
  sameGenre: "В зачёт идут отдельные фильмы одного и того же жанра; один фильм даёт одно деление.",
  decades: "Каждый отдельный фильм из подходящего десятилетия даёт одно деление.",
  uniqueDecades: "Каждое деление требует отдельного фильма из нового подходящего десятилетия.",
  actors: "В зачёт идут отдельные фильмы с одним и тем же подходящим актёром.",
  directors: "В зачёт идут отдельные фильмы одного и того же подходящего режиссёра.",
  composers: "В зачёт идут отдельные фильмы одного и того же подходящего композитора.",
  years: "Каждый отдельный фильм подходящего года даёт одно деление.",
  uniqueYears: "Для разных годов каждое деление требует отдельного фильма нового подходящего года.",
  runtime: "Суммируется хронометраж, но один фильм добавляет не больше одного часа и одного деления.",
  media: "Один отдельно оценённый фильм или сериал даёт не больше одного деления; сезоны одного сериала не дублируют прогресс.",
  ratings: "Каждая подходящая оценка отдельного фильма или сериала даёт одно деление.",
  languages: "Учитывается язык оригинала, а не языки дубляжа или перевода: нужны разные языки и отдельный фильм для каждого языка.",
  mixed: "Фильм закрепляется только за одним из перечисленных условий; повторяющиеся страны, жанры, годы и персоны не дублируют прогресс."
};

const getChallengeScoringRule = (type: keyof typeof CHALLENGE_TYPES, description: string) => {
  const normalizedDescription = normalizeText(description);
  if (type === "activity" && normalizedDescription.includes("разных дней")) {
    return CHALLENGE_SCORING_RULES.activityDays;
  }
  if (type === "countries" && /открыть|стран/.test(normalizedDescription)) {
    return CHALLENGE_SCORING_RULES.uniqueCountries;
  }
  if (type === "genres") {
    if (normalizedDescription.includes("одного жанра")) return CHALLENGE_SCORING_RULES.sameGenre;
    if (/разн|открыть|закрыть/.test(normalizedDescription)) return CHALLENGE_SCORING_RULES.uniqueGenres;
  }
  if (type === "decades" && /разн|открыть/.test(normalizedDescription)) {
    return CHALLENGE_SCORING_RULES.uniqueDecades;
  }
  if (type === "years" && /разн|открыть|каждый год/.test(normalizedDescription)) {
    return CHALLENGE_SCORING_RULES.uniqueYears;
  }
  return CHALLENGE_SCORING_RULES[type];
};

export interface ChallengeDefinition {
  id: string;
  title: string;
  type: keyof typeof CHALLENGE_TYPES;
  description: string;
  scoringRule: string;
  target: number;
  durationDays: number;
  difficulty: keyof typeof CHALLENGE_DIFFICULTIES;
  experience: number;
  getProgress: (ratings: Rating[]) => number;
}

const challenge = (
  id: string, title: string, type: ChallengeDefinition["type"], description: string,
  target: number, durationDays: number, difficulty: ChallengeDefinition["difficulty"],
  getProgress: ChallengeDefinition["getProgress"]
): ChallengeDefinition => ({
  id,
  title,
  type,
  description,
  scoringRule: getChallengeScoringRule(type, description),
  target,
  durationDays,
  difficulty,
  experience: getChallengeExperience(difficulty),
  getProgress: (ratings: Rating[]) => getProgress(deduplicateRatingsByMediaId(ratings))
});

export const CHALLENGES = [
  challenge("first_step", "Первый шаг", "activity", "Посмотреть 1 фильм", 1, 7, "easy", count()),
  challenge("double_feature", "Двойной сеанс", "activity", "Посмотреть 2 фильма", 2, 7, "easy", count()),
  challenge("cinephile_passport", "Паспорт киномана", "countries", "Открыть 1 страну", 1, 14, "easy", uniqueCountries),
  challenge("genre_sample", "Жанровая проба", "genres", "Посмотреть 2 разных жанра", 2, 14, "easy", uniqueGenres),
  challenge("time_jump", "Прыжок во времени", "decades", "Посмотреть кино из 2 разных десятилетий", 2, 21, "easy", uniqueDecades),
  challenge("new_face", "Новое лицо", "actors", "Посмотреть 2 фильма с одним актёром", 2, 21, "easy", sameActor),
  challenge("first_acquaintance", "Первое знакомство", "directors", "Посмотреть 2 фильма одного режиссёра", 2, 21, "easy", sameDirector),
  challenge("soft_start", "Мягкий старт", "activity", "Посмотреть 4 фильма", 4, 30, "easy", count()),
  challenge("one_step_map", "Один шаг за карту", "countries", "Посмотреть фильм из любой страны", 1, 7, "easy", uniqueCountries),
  challenge("slow_cinema", "Кино без спешки", "activity", "Посмотреть 3 фильма", 3, 21, "easy", count()),
  challenge("weekend_warmup", "Разогрев выходного дня", "activity", "Посмотреть 2 фильма за короткий срок", 2, 3, "easy", count()),
  challenge("good_start", "Хорошее начало", "ratings", "Поставить 2 оценки 8+", 2, 14, "easy", highRatingCount(8)),
  challenge("single_comedy", "Лёгкий смешок", "genres", "Посмотреть комедию", 1, 14, "easy", genreCount("комедия", "comedy")),
  challenge("single_drama", "Драматическая пауза", "genres", "Посмотреть драму", 1, 14, "easy", genreCount("драма", "drama")),
  challenge("passport_asia", "Штамп Азии", "countries", "Посмотреть фильм из Азии", 1, 14, "easy", groupFilmCount("asia")),
  challenge("passport_europe", "Штамп Европы", "countries", "Посмотреть фильм из Европы", 1, 14, "easy", groupFilmCount("europe")),
  challenge("classic_touch", "Классическое касание", "years", "Посмотреть фильм до 1980 года", 1, 21, "easy", yearRangeCount(1895, 1979)),
  challenge("modern_touch", "Современный штрих", "years", "Посмотреть фильм после 2010 года", 1, 14, "easy", yearRangeCount(2010, 2026)),
  challenge("long_evening", "Длинный вечер", "runtime", "Посмотреть фильм от 150 минут", 1, 21, "easy", runtimeCount(150)),
  challenge("movie_only_start", "Чистый кинозал", "media", "Посмотреть 2 полнометражных фильма", 2, 14, "easy", mediaCount("movie")),

  challenge("cinema_week", "Кинонеделя", "activity", "Посмотреть 3 фильма", 3, 7, "medium", count()),
  challenge("steady_viewer", "Стабильный зритель", "activity", "Посмотреть 8 фильмов", 8, 30, "medium", count()),
  challenge("genre_scout", "Жанровая разведка", "genres", "Открыть 5 разных жанров", 5, 30, "medium", uniqueGenres),
  challenge("small_journey", "Малое путешествие", "countries", "Открыть 3 новые страны", 3, 30, "medium", uniqueCountries),
  challenge("european_holiday", "Европейские каникулы", "countries", "Посмотреть фильмы из 3 стран Европы", 3, 45, "medium", uniqueCountriesIn("europe")),
  challenge("eastern_express", "Восточный экспресс", "countries", "Посмотреть фильмы из 3 стран Азии", 3, 45, "medium", uniqueCountriesIn("asia")),
  challenge("comedy_therapy", "Комедийная терапия", "genres", "Посмотреть 4 комедии", 4, 30, "medium", genreCount("комедия", "comedy")),
  challenge("fear_week", "Неделя страха", "genres", "Посмотреть 3 хоррора", 3, 21, "medium", genreCount("ужасы", "horror")),
  challenge("masters_student", "Ученик мастера", "directors", "Посмотреть 3 фильма одного режиссёра", 3, 30, "medium", sameDirector),
  challenge("time_machine", "Машина времени", "decades", "Открыть 5 разных десятилетий", 5, 45, "medium", uniqueDecades),
  challenge("fan_club", "Фан-клуб", "actors", "Посмотреть 5 фильмов с одним актёром", 5, 45, "medium", sameActor),
  challenge("outside_hollywood", "За пределами Голливуда", "countries", "Посмотреть 5 фильмов не из США", 5, 45, "medium", nonUsFilms),
  challenge("drama_path", "Драматическая линия", "genres", "Посмотреть 5 драм", 5, 30, "medium", genreCount("драма", "drama")),
  challenge("animation_corner", "Уголок анимации", "genres", "Посмотреть 4 мультфильма", 4, 30, "medium", genreCount("мультфильм", "animation")),
  challenge("family_evening", "Семейный вечер", "genres", "Посмотреть 4 семейных фильма", 4, 30, "medium", genreCount("семейный", "family")),
  challenge("documentary_window", "Документальное окно", "genres", "Посмотреть 3 документальных фильма", 3, 30, "medium", genreCount("документальный", "documentary")),
  challenge("sci_fi_route", "Маршрут фантастики", "genres", "Посмотреть 4 фантастических фильма", 4, 30, "medium", genreCount("фантастика", "science fiction", "sci-fi", "sci fi")),
  challenge("crime_file", "Криминальное дело", "genres", "Посмотреть 4 криминальных или детективных фильма", 4, 30, "medium", genreCount("криминал", "crime", "детектив", "mystery")),
  challenge("high_score_collection", "Полка высоких оценок", "ratings", "Поставить 15 оценок 8+", 15, 45, "medium", highRatingCount(8)),
  challenge("three_languages", "Три языка", "languages", "Посмотреть фильмы на 3 языках оригинала", 3, 45, "medium", uniqueLanguages),
  challenge("composer_theme", "Музыкальная тема", "composers", "Посмотреть 3 фильма одного композитора", 3, 45, "medium", sameComposer),
  challenge("classic_sampler", "Пробник классики", "years", "Посмотреть 3 фильма до 1980 года", 3, 45, "medium", yearRangeCount(1895, 1979)),
  challenge("new_millennium", "Новое тысячелетие", "years", "Посмотреть 5 фильмов 2000-х и новее", 5, 30, "medium", yearRangeCount(2000, 2026)),
  challenge("series_trial", "Серийная проба", "media", "Посмотреть 3 сериала", 3, 30, "medium", mediaCount("tv")),
  challenge("runtime_10_hours", "10 часов кино", "runtime", "Набрать 10 часов просмотра", 10, 30, "medium", totalRuntimeHours),
  challenge("french_postcard", "Французская открытка", "countries", "Посмотреть 3 фильма из Франции", 3, 45, "medium", countryFilmCount("франция", "france", "fr")),
  challenge("korean_signal", "Корейский сигнал", "countries", "Посмотреть 3 фильма из Южной Кореи", 3, 45, "medium", countryFilmCount("южная корея", "south korea", "korea", "kr")),
  challenge("genre_triangle", "Жанровый треугольник", "genres", "Закрыть драму, комедию и триллер", 3, 30, "medium", genreSet(["драма", "drama"], ["комедия", "comedy"], ["триллер", "thriller"])),

  challenge("almost_no_weekends", "Почти без выходных", "activity", "Посмотреть 7 фильмов", 7, 10, "hard", count()),
  challenge("cinema_month", "Месяц кино", "activity", "Посмотреть 20 фильмов", 20, 30, "hard", count()),
  challenge("rapid_fifteen", "Быстрые пятнадцать", "activity", "Посмотреть 15 фильмов за 2 недели", 15, 14, "hard", count()),
  challenge("thriller_marathon", "Триллерный марафон", "genres", "Посмотреть 15 триллеров", 15, 45, "hard", genreCount("триллер", "thriller")),
  challenge("genre_master", "Мастер жанра", "genres", "Посмотреть 15 фильмов одного жанра", 15, 45, "hard", maxFrequency(getGenres)),
  challenge("around_world", "Вокруг света", "countries", "Открыть 12 стран", 12, 60, "hard", uniqueCountries),
  challenge("latin_expedition", "Латиноамериканская экспедиция", "countries", "Открыть 5 стран Южной Америки", 5, 60, "hard", uniqueCountriesIn("southAmerica")),
  challenge("immersion", "Погружение", "directors", "Посмотреть 7 фильмов одного режиссёра", 7, 60, "hard", sameDirector),
  challenge("century_witness", "Свидетель века", "decades", "Открыть 8 десятилетий", 8, 75, "hard", uniqueDecades),
  challenge("career_microscope", "Карьера под микроскопом", "actors", "Посмотреть 10 фильмов с одним актёром", 10, 60, "hard", sameActor),
  challenge("no_repeats", "Ни одного повтора", "genres", "Открыть 10 разных жанров", 10, 60, "hard", uniqueGenres),
  challenge("europe_tour", "Кинотур по Европе", "countries", "Открыть 10 стран Европы", 10, 75, "hard", uniqueCountriesIn("europe")),
  challenge("asia_tour", "Кинотур по Азии", "countries", "Открыть 10 стран Азии", 10, 75, "hard", uniqueCountriesIn("asia")),
  challenge("african_postcards", "Африканские открытки", "countries", "Открыть 5 стран Африки", 5, 75, "hard", uniqueCountriesIn("africa")),
  challenge("north_america_route", "Североамериканский маршрут", "countries", "Открыть 5 стран Северной Америки", 5, 60, "hard", uniqueCountriesIn("northAmerica")),
  challenge("composer_marathon", "Композиторский марафон", "composers", "Посмотреть 7 фильмов одного композитора", 7, 60, "hard", sameComposer),
  challenge("author_week", "Авторская неделя", "directors", "Посмотреть 5 фильмов одного режиссёра за 3 недели", 5, 21, "hard", sameDirector),
  challenge("actor_streak", "Актёрская серия", "actors", "Посмотреть 7 фильмов с одним актёром", 7, 30, "hard", sameActor),
  challenge("nine_point_shelf", "Девятки на полке", "ratings", "Поставить 8 оценок 9+", 8, 60, "hard", highRatingCount(9)),
  challenge("classic_dive", "Погружение в классику", "years", "Посмотреть 10 фильмов до 1980 года", 10, 60, "hard", yearRangeCount(1895, 1979)),
  challenge("seventies_flashback", "Флэшбек семидесятых", "decades", "Посмотреть 5 фильмов 1970-х", 5, 45, "hard", decadeFilmCount(1970)),
  challenge("modern_archive", "Архив XXI века", "years", "Посмотреть 15 фильмов 2000-х и новее", 15, 45, "hard", yearRangeCount(2000, 2026)),
  challenge("runtime_30_hours", "30 часов кино", "runtime", "Набрать 30 часов просмотра", 30, 60, "hard", totalRuntimeHours),
  challenge("series_season", "Сезон сериалов", "media", "Посмотреть 10 сериалов", 10, 60, "hard", mediaCount("tv")),
  challenge("movie_only_marathon", "Только кино", "media", "Посмотреть 25 полнометражных фильмов", 25, 45, "hard", mediaCount("movie")),
  challenge("japanese_path", "Японская тропа", "countries", "Посмотреть 7 фильмов из Японии", 7, 60, "hard", countryFilmCount("япония", "japan", "jp")),
  challenge("high_rating_archive", "Архив любимого", "ratings", "Поставить 40 оценок 8+", 40, 120, "hard", highRatingCount(8)),
  challenge("perfect_shelf", "Полка девяток", "ratings", "Поставить 25 оценок 9+", 25, 180, "hard", highRatingCount(9)),
  challenge("perfect_tens", "Полка десяток", "ratings", "Поставить 10 оценок 10/10", 10, 180, "hard", exactRatingCount(10)),
  challenge("genre_pentagon", "Жанровый пятиугольник", "genres", "Посмотреть фильмы пяти жанров: драма, комедия, триллер, фантастика и мультфильм", 5, 45, "hard", genreSet(["драма", "drama"], ["комедия", "comedy"], ["триллер", "thriller"], ["фантастика", "science fiction", "sci-fi"], ["мультфильм", "animation"])),

  challenge("cinephile_season", "Сезон киномана", "activity", "Посмотреть 75 фильмов", 75, 90, "extreme", count()),
  challenge("life_and_work", "Жизнь и творчество", "directors", "Посмотреть 15 фильмов одного режиссёра", 15, 90, "extreme", sameDirector),
  challenge("continent_conqueror", "Покоритель континента", "countries", "Закрыть любой континент", 1, 120, "extreme", closedContinent),
  challenge("world_citizen", "Гражданин мира", "countries", "Открыть 25 стран", 25, 120, "extreme", uniqueCountries),
  challenge("era_witness", "Свидетель эпох", "decades", "Открыть 12 десятилетий", 12, 120, "extreme", uniqueDecades),
  challenge("obsession", "Одержимость", "actors", "Посмотреть 25 фильмов с одним актёром", 25, 120, "extreme", sameActor),
  challenge("year_by_year", "Год за годом", "years", "Открыть 30 разных годов выпуска", 30, 120, "extreme", uniqueYears),
  challenge("film_archive", "Киноархив", "activity", "Посмотреть 120 фильмов", 120, 180, "extreme", count()),
  challenge("movie_binge", "Кинозапой", "activity", "Посмотреть 30 фильмов за месяц", 30, 30, "extreme", count()),
  challenge("silent_era_probe", "След немого кино", "years", "Посмотреть 10 фильмов до 1940 года", 10, 180, "extreme", yearRangeCount(1895, 1939)),
  challenge("golden_age", "Золотой век", "years", "Посмотреть 20 фильмов 1940-1979 годов", 20, 120, "extreme", yearRangeCount(1940, 1979)),
  challenge("twenty_first_century", "Большой XXI век", "years", "Посмотреть 50 фильмов 2000-х и новее", 50, 90, "extreme", yearRangeCount(2000, 2026)),
  challenge("global_sampler", "Пробник мира", "mixed", "5 стран Европы, 5 Азии, 3 Северной и 3 Южной Америки", 16, 120, "extreme", worldSampler),
  challenge("polyglot", "Кинополиглот", "languages", "Посмотреть фильмы на 10 языках оригинала", 10, 120, "extreme", uniqueLanguages),
  challenge("composer_devotion", "Верность композитору", "composers", "Посмотреть 15 фильмов одного композитора", 15, 120, "extreme", sameComposer),
  challenge("director_retrospective", "Режиссёрская ретроспектива", "directors", "Посмотреть 25 фильмов одного режиссёра", 25, 180, "extreme", sameDirector),
  challenge("actor_legend", "Актёрская легенда", "actors", "Посмотреть 40 фильмов с одним актёром", 40, 180, "extreme", sameActor),
  challenge("runtime_100_hours", "100 часов кино", "runtime", "Набрать 100 часов просмотра", 100, 180, "extreme", totalRuntimeHours),
  challenge("genre_collector_extreme", "Коллекционер жанров", "genres", "Открыть 18 разных жанров", 18, 180, "extreme", uniqueGenres),
  challenge("southern_cross", "Южный крест", "countries", "Открыть 8 стран Океании", 8, 180, "extreme", uniqueCountriesIn("oceania")),
  challenge("africa_deep_dive", "Глубокая Африка", "countries", "Открыть 12 стран Африки", 12, 180, "extreme", uniqueCountriesIn("africa")),
  challenge("non_us_season", "Сезон без США", "countries", "Посмотреть 50 фильмов не из США", 50, 120, "extreme", nonUsFilms),
  challenge("curator_path", "Путь куратора", "mixed", "10 оценок 8+, 10 жанров и 10 стран", 30, 120, "extreme", curatorPath),
  challenge("every_day_movie", "Ни дня без фильма", "activity", "Смотреть фильмы в 30 разных дней", 30, 30, "extreme", unique((rating: Rating) => [getRatingDay(rating)])),
  challenge("two_per_day", "Два фильма в день", "activity", "Посмотреть 60 фильмов", 60, 30, "extreme", count()),
  challenge("global_expedition", "Глобальная экспедиция", "countries", "Открыть 50 стран", 50, 180, "extreme", uniqueCountries),
  challenge("absolute_marathon", "Абсолютный марафон", "activity", "Посмотреть 180 фильмов", 180, 180, "extreme", count()),
  challenge("living_encyclopedia", "Живая энциклопедия", "mixed", "100 фильмов, 50 стран, 25 жанров и 25 годов", 200, 240, "extreme", livingEncyclopedia),
  challenge("year_grand_tour", "Большой тур по годам", "years", "Открыть 60 разных годов выпуска", 60, 365, "extreme", uniqueYears),
  challenge("world_without_borders", "Мир без границ", "countries", "Открыть 75 стран", 75, 365, "extreme", uniqueCountries),
  challenge("genre_omnivore", "Жанровый всеядный", "genres", "Открыть 19 разных жанров", 19, 365, "extreme", uniqueGenres),
  challenge("century_marathon", "Марафон века", "years", "Посмотреть 100 фильмов до 2000 года", 100, 365, "extreme", yearRangeCount(1895, 1999)),
  challenge("full_globe_sampler", "Большая карта мира", "mixed", "10 стран Европы, 10 Азии, 5 Северной Америки, 5 Южной Америки и 5 Африки", 35, 365, "extreme", grandWorldSampler),

  challenge("three_per_day", "Три фильма в день", "activity", "Посмотреть 90 фильмов", 90, 30, "extreme", count()),
  challenge("365_cinema_days", "365 дней кино", "activity", "Посмотреть 365 фильмов", 365, 365, "hard", count()),
  challenge("130_years", "130 лет кино", "years", "Посмотреть по фильму за каждый год начиная с 1895", 132, 365, "impossible", (ratings: Rating[]) => new Set(ratings.map(getYear).filter((year) => Number(year) >= 1895 && Number(year) <= 2026)).size),
  challenge("cinematic_universe", "Киновселенная", "mixed", "20 стран, 20 жанров, 10 десятилетий и 20 режиссёров", 70, 365, "impossible", mixedUniverse),
  challenge("cinema_titan", "Кинотитан", "activity", "Посмотреть 500 фильмов", 500, 365, "hard", count()),
  challenge("director_lifetime", "Жизнь с автором", "directors", "Посмотреть 50 фильмов одного режиссёра", 50, 365, "impossible", sameDirector),
  challenge("actor_lifetime", "Жизнь с актёром", "actors", "Посмотреть 75 фильмов с одним актёром", 75, 365, "impossible", sameActor),
  challenge("composer_lifetime", "Симфония длиною в год", "composers", "Посмотреть 40 фильмов одного композитора", 40, 365, "impossible", sameComposer),
  challenge("runtime_500_hours", "500 часов кино", "runtime", "Набрать 500 часов просмотра", 500, 365, "impossible", totalRuntimeHours),
  challenge("language_atlas", "Атлас языков", "languages", "Посмотреть фильмы на 25 языках оригинала", 25, 365, "impossible", uniqueLanguages),
  challenge("cinema_immortal", "Кинобессмертный", "activity", "Посмотреть 5000 фильмов", 5000, 365, "impossible", count()),
  challenge("near_complete_world", "Почти весь мир", "countries", "Открыть 200 стран и территорий", 200, 730, "impossible", uniqueCountries)
];

export const getChallengeById = (id: string) => CHALLENGES.find((item) => item.id === id);
